import {
  useScreen,
  useSignupPassword,
} from "@auth0/auth0-acul-react/signup-password";
import type {
  ScreenMembersOnSignupPassword,
  SignupPasswordOptions,
} from "@auth0/auth0-acul-react/types";

import { detectFlow, type SignupFlow } from "@/utils/flow";
import { executeSafely } from "@/utils/helpers/executeSafely";
import {
  directStashConfig,
  StashError,
  stashNonIdentityFields,
} from "@/utils/signupStash";

import locales from "../locales/en.json";

// Everything the 3-sub-step wizard collects (Personal info -> Set password
// -> Check details), submitted in ONE signup() call when Join is clicked.
// See ARCHITECTURE.md "Member Signup Flow" for why this is deliberately a
// single client-side wizard rather than multiple screen round-trips: no
// Auth0 account exists until this call succeeds, so mid-wizard abandonment
// (including after email OTP) leaves no trace.
// Flow B collects only the identity essentials, so everything Flow A defers to
// progressive profiling is optional here and omitted from the submission. See
// ARCHITECTURE.md "Signup Flows (A / B / C)".
export interface WizardFields {
  flow: SignupFlow;
  password: string;
  firstName: string;
  lastName: string;
  mobile: string;
  // Flow A only — deferred by Flow B.
  gender?: string;
  title?: string;
  dateOfBirth?: string;
  country?: string;
  residentialAddress?: string;
  postalAddress?: string;
  tfn?: string;
  consentAccepted?: boolean;
  // When the member ticked the step-5 declaration (Join). Only used by the
  // direct-stash path, which records it inside the encrypted payload.
  declarationAcceptedAt?: string;
  // Solved bot-detection token. Supplied by the terminal wizard sub-step via
  // useCaptcha — NOT by a react-hook-form field, because the widget providers
  // do not register one. See the comment in SignupPasswordForm.tsx.
  captcha?: string;
}

// What a Join attempt resolved to, for the form. "stash_failed" means the
// non-identity data could not be handed to the API, so the Auth0 signup was
// deliberately NOT submitted (see the direct-stash branch below).
export type SignupSubmitResult = { status: "submitted" } | { status: "stash_failed"; code: string };

export const useSignupPasswordManager = () => {
  const signupPassword = useSignupPassword();

  const screen: ScreenMembersOnSignupPassword = useScreen();

  // Runtime flow detection — the client ID is the only discriminator.
  const flow: SignupFlow = detectFlow(signupPassword.client?.id);

  const { isCaptchaAvailable, texts, editLink, captcha } = screen;

  const handleSignupPassword = async (fields: WizardFields): Promise<SignupSubmitResult> => {
    const options: SignupPasswordOptions = {
      password: fields.password,
    };

    if (screen.data?.email) {
      options.email = screen.data.email as string;
    }
    if (screen.data?.phoneNumber) {
      options.phoneNumber = screen.data.phoneNumber as string;
    }
    if (screen.data?.username) {
      options.username = screen.data.username as string;
    }
    // Only sent when Auth0 actually challenged this screen. Mirrors
    // useSignupIdManager: an empty key is worse than an absent one.
    if (screen.isCaptchaAvailable && fields.captcha?.trim()) {
      options.captcha = fields.captcha.trim();
    }

    // Submitted with the "ulp-" keys "Capture Signup Profile Fields" reads
    // from event.request.body — see ARCHITECTURE.md "Member Signup Flow".
    // The SDK's FormHandler posts every key of `options` as a literal hidden
    // form field, so these reach the Action unchanged.
    // Captured by both flows.
    options["ulp-first-name"] = fields.firstName.trim();
    options["ulp-last-name"] = fields.lastName.trim();
    options["ulp-mobile"] = fields.mobile.trim();

    // Flow A, direct stash (ARCHITECTURE.md, "Signup direct stash"): the
    // non-identity fields go to the API encrypted, and Auth0 receives only
    // the identity fields above plus an opaque ref. If the stash fails we
    // stop HERE — submitting anyway would create an Auth0 identity with no
    // member data behind it.
    const stash = directStashConfig();
    if (fields.flow !== "B" && stash.enabled) {
      const email = screen.data?.email as string | undefined;
      if (!email) return { status: "stash_failed", code: "no_email" };
      try {
        options["ulp-stash-ref"] = await stashNonIdentityFields(
          {
            email,
            dateOfBirth: fields.dateOfBirth ?? "",
            gender: fields.gender ?? "",
            title: fields.title ?? "",
            country: fields.country ?? "",
            residentialAddress: fields.residentialAddress?.trim() ?? "",
            postalAddress: fields.postalAddress?.trim() || null,
            tfn: fields.tfn?.trim() || null,
            declarationAccepted: fields.consentAccepted === true,
            declarationAcceptedAt: fields.declarationAcceptedAt ?? new Date().toISOString(),
          },
          { config: stash }
        );
      } catch (err) {
        return { status: "stash_failed", code: err instanceof StashError ? err.code : "unknown" };
      }
    } else if (fields.flow !== "B") {
      // Flow A, legacy path (flag off): everything goes to Auth0 as ulp-*
      // fields and the relay Action forwards it from user_metadata. Flow B
      // omits these keys entirely rather than sending empty strings — the
      // capture Action's v2 branch does not look for them, and an empty
      // string would land in user_metadata and then relay through as a real
      // value.
      options["ulp-date-of-birth"] = fields.dateOfBirth;
      options["ulp-gender"] = fields.gender;
      options["ulp-title"] = fields.title;
      options["ulp-country"] = fields.country;
      options["ulp-residential-address"] = fields.residentialAddress?.trim();
      if (fields.postalAddress?.trim()) {
        options["ulp-postal-address"] = fields.postalAddress.trim();
      }
      if (fields.tfn?.trim()) {
        options["ulp-tfn"] = fields.tfn.trim();
      }
      if (fields.consentAccepted) {
        options["ulp-consent"] = "on";
      }
    }

    // Password/TFN redacted from logs
    const logOptions = {
      ...options,
      password: "[REDACTED]",
      "ulp-tfn": options["ulp-tfn"] ? "[REDACTED]" : undefined,
    };

    executeSafely(
      `Signup password with options: ${JSON.stringify(logOptions)}`,
      () => signupPassword.signup(options)
    );
    return { status: "submitted" };
  };

  return {
    signupPassword,
    handleSignupPassword,
    flow,
    texts,
    isCaptchaAvailable,
    editLink,
    captcha,
    screenData: screen.data,
    locales,
  };
};
